// Core
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import {
  Crumbs,
  DataTable,
  DateRangePopover,
  KpiStrip,
  LineChart,
  PageHead,
  SectionCard,
  SideTabs,
  Tabs,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Pages
import MarketSessionsTable from "pages/ReadMarket/MarketSessionsTable";
import ListTransactionAdjustments from "pages/ReadTransactionAdjustments";
import { BilateralAllocationsSection } from "pages/ManageBilateralEnergyAllocations";

// Context
import { AbilityContext } from "context/Ability.context";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectTimeSelector, updateTimeSelector } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetMarketMetricsByCommunityIdQuery } from "state/sessions/sessionApiSlice";
import { useGetOMIEDataBetweenDatesQuery } from "state/omie/omieApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";
import { createChartXAxisLabel } from "utils/charts";
import { browserLocale } from "utils/time";

const fmt = (v, d = 2) =>
  v != null ? Number(v).toLocaleString(browserLocale(), { maximumFractionDigits: d }) : "—";

// The price chart shows daily trends, regardless of the selected range.
const MARKET_GRANULARITY = "DAY";

function MarketAnalytics() {
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["market"]);
  const { startDate, endDate } = useSelector(selectTimeSelector);

  const queryArgs = { communityId: community.id, startDate, endDate };
  // Refetch on range change only — refetchOnFocus caused the chart to re-fetch
  // and re-animate (flicker) whenever the window regained focus.
  const queryOpts = { refetchOnMountOrArgChange: true };

  const { data: allMarket } = useGetMarketMetricsByCommunityIdQuery(
    { ...queryArgs, granularity: MARKET_GRANULARITY },
    queryOpts
  );
  const { data: omieData } = useGetOMIEDataBetweenDatesQuery(
    { ...queryArgs, granularity: MARKET_GRANULARITY },
    queryOpts
  );

  const averagePricesList = useMemo(
    () =>
      (allMarket?.average_prices_list || []).map((metric) => ({
        ...metric,
        price: metric.price === 0 ? undefined : metric.price,
      })),
    [allMarket]
  );

  // Overlay the OMIE price (€/MWh → €/kWh) on the internal-price timeline
  const omieByTs = useMemo(() => {
    const map = {};
    (omieData || []).forEach((item) => {
      map[item.datetime_timestamp] = item.price / 1000;
    });
    return map;
  }, [omieData]);

  const labels = useMemo(
    () =>
      averagePricesList.map((m) => createChartXAxisLabel(m.datetime_timestamp, MARKET_GRANULARITY)),
    [averagePricesList]
  );
  const chartTimestamps = useMemo(
    () => averagePricesList.map((m) => m.datetime_timestamp),
    [averagePricesList]
  );
  const series = useMemo(
    () => [
      {
        name: t("analytics.charts.series_internal", { defaultValue: "Preço interno" }),
        data: averagePricesList.map((m) => (m.price != null ? +Number(m.price).toFixed(4) : null)),
        color: "var(--accent)",
      },
      {
        name: t("analytics.charts.series_omie", { defaultValue: "OMIE" }),
        data: averagePricesList.map((m) => {
          const v = omieByTs[m.datetime_timestamp];
          return v != null ? +v.toFixed(4) : null;
        }),
        color: "var(--info)",
      },
    ],
    [averagePricesList, omieByTs, t]
  );

  const memberRows = (allMarket?.member_market_metrics || [])
    .filter((item) => item.member_name !== undefined)
    .map((item, i) => ({
      id: i,
      member: item.member_name,
      energy_bought: item.energy_bought,
      energy_sold: item.energy_sold,
    }));

  const memberResourceRows = (allMarket?.member_resource_market_metrics || [])
    .filter((item) => item.member_name !== undefined)
    .map((item, i) => ({
      id: i,
      member: item.member_name,
      resource: item.resource_name ?? item.resource ?? "—",
      energy_bought: item.energy_bought,
      energy_sold: item.energy_sold,
    }));

  const transactionTotalRows = (allMarket?.transactions_total || []).map((item, i) => ({
    id: i,
    provider: item.provider_member_name,
    receiver: item.receiver_member_name,
    energy: item.e_lem,
    average_price: item.p_lem,
    amount: item.rev_lem,
  }));

  const num = (v, color, decimals = 3) => (
    <span className="tabular-nums" style={color ? { color, fontWeight: 600 } : undefined}>
      {fmt(v, decimals)}
    </span>
  );

  const memberCell = (v) => (
    <div className="rec-cell-member">
      <div className="rec-av-sm" style={{ background: avColor(v) }}>
        {initials(v)}
      </div>
      <b>{v}</b>
    </div>
  );

  const memberCols = [
    { key: "member", label: t("analytics.tables.columns.member"), render: memberCell },
    {
      key: "energy_bought",
      label: t("analytics.tables.columns.energy_bought"),
      align: "right",
      render: (v) => num(v, "var(--info)"),
    },
    {
      key: "energy_sold",
      label: t("analytics.tables.columns.energy_sold"),
      align: "right",
      render: (v) => num(v, "var(--accent)"),
    },
  ];

  const memberResourceCols = [
    { key: "member", label: t("analytics.tables.columns.member"), render: memberCell },
    { key: "resource", label: t("analytics.tables.columns.resource") },
    {
      key: "energy_bought",
      label: t("analytics.tables.columns.energy_bought"),
      align: "right",
      render: (v) => num(v, "var(--info)"),
    },
    {
      key: "energy_sold",
      label: t("analytics.tables.columns.energy_sold"),
      align: "right",
      render: (v) => num(v, "var(--accent)"),
    },
  ];

  const transactionTotalCols = [
    {
      key: "provider",
      label: t("analytics.tables.columns.provider"),
      render: memberCell,
    },
    {
      key: "receiver",
      label: t("analytics.tables.columns.receiver"),
      render: memberCell,
    },
    {
      key: "energy",
      label: t("analytics.tables.columns.energy"),
      align: "right",
      render: (v) => num(v),
    },
    {
      key: "average_price",
      label: t("analytics.tables.columns.average_price"),
      align: "right",
      render: (v) => num(v, undefined, 4),
    },
    {
      key: "amount",
      label: t("analytics.tables.columns.amount"),
      align: "right",
      render: (v) => num(v, "var(--accent)", 2),
    },
  ];

  return (
    <div className="flex flex-col gap-rec">
      <KpiStrip
        cols={2}
        items={[
          {
            icon: "sell",
            value: fmt(allMarket?.average_price, 4),
            unit: "€/kWh",
            label: t("analytics.key_metrics.average_price"),
            tint: "teal",
          },
          {
            icon: "bolt",
            value: fmt(allMarket?.transacted_energy),
            unit: "kWh",
            label: t("analytics.key_metrics.transacted_energy"),
            tint: "amber",
          },
        ]}
      />

      <SectionCard
        id="market-price"
        icon="query_stats"
        title={t("analytics.charts.avg_price_vs_omie")}
      >
        <div className="flex items-center gap-[14px] flex-wrap mb-2 text-[12px] text-ink-2">
          <span className="inline-flex items-center gap-[6px]">
            <i
              className="inline-block w-[9px] h-[9px] rounded-[3px]"
              style={{ background: "var(--accent)" }}
            />
            {t("analytics.charts.series_internal", { defaultValue: "Preço interno" })}
          </span>
          <span className="inline-flex items-center gap-[6px]">
            <i
              className="inline-block w-[9px] h-[9px] rounded-[3px]"
              style={{ background: "var(--info)" }}
            />
            {t("analytics.charts.series_omie", { defaultValue: "OMIE" })}
          </span>
        </div>
        <LineChart
          series={series}
          labels={labels}
          timestamps={chartTimestamps}
          unit="€"
          height={340}
          type="area"
          xTitle={t("charts.axis_date", { ns: "common" })}
          yTitle="€/kWh"
          showLegend={false}
        />
      </SectionCard>

      <div className="rec-card">
        <div className="rec-card-pad pb-0">
          <h3 className="rec-section-title">
            <Icon name="groups" />
            {t("analytics.tables.titles.by_member", { defaultValue: "Mercado por membro" })}
          </h3>
        </div>
        <DataTable
          columns={memberCols}
          rows={memberRows}
          search={t("analytics.tables.search_member", { defaultValue: "Procurar membro…" })}
          searchKeys={["member"]}
          maxRows={8}
        />
      </div>

      <div className="rec-card">
        <div className="rec-card-pad pb-0">
          <h3 className="rec-section-title">
            <Icon name="solar_power" />
            {t("analytics.tables.titles.by_member_resource", {
              defaultValue: "Mercado por membro e recurso",
            })}
          </h3>
        </div>
        <DataTable
          columns={memberResourceCols}
          rows={memberResourceRows}
          search={t("analytics.tables.search_member", { defaultValue: "Procurar membro…" })}
          searchKeys={["member", "resource"]}
          maxRows={8}
        />
      </div>

      <div className="rec-card">
        <div className="rec-card-pad pb-0">
          <h3 className="rec-section-title">
            <Icon name="swap_horiz" />
            {t("analytics.tables.titles.transactions_total")}
          </h3>
        </div>
        <DataTable
          columns={transactionTotalCols}
          rows={transactionTotalRows}
          search={t("analytics.tables.search_transactions")}
          searchKeys={["provider", "receiver"]}
          maxRows={8}
        />
      </div>
    </div>
  );
}

function ReadMarket() {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const ability = useContext(AbilityContext);
  const { t } = useTranslation(["market", "community"]);
  const { startDate, endDate, granularity, horizon, isExpanded } = useSelector(selectTimeSelector);

  const [side, setSide] = useState("market");
  const [marketTab, setMarketTab] = useState("charts");

  const sideTabs = [
    { id: "market", icon: "query_stats", label: t("page.title") },
    { id: "adjustments", icon: "tune", label: t("page.tabs.adjustments") },
    // The allocations route is admin-only; hide the tab from other roles.
    ...(ability.can("manage", "all")
      ? [
          {
            id: "bilateral",
            icon: "swap_horiz",
            label: t("bilateralEnergyAllocations.title", { ns: "community" }),
          },
        ]
      : []),
  ];

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="market" />
      <Crumbs items={[{ label: community?.name || "—" }, { label: t("page.title") }]} />
      <PageHead
        title={t("page.title")}
        subtitle={t("page.subtitle")}
        actions={
          <div className="flex items-center gap-[10px] flex-wrap">
            <DateRangePopover
              value={{ start: startDate, end: endDate }}
              onChange={(v) =>
                dispatch(
                  updateTimeSelector({
                    startDate: v.start,
                    endDate: v.end,
                    granularity,
                    horizon,
                    isExpanded,
                  })
                )
              }
            />
          </div>
        }
      />

      <SideTabs items={sideTabs} active={side} onChange={setSide}>
        {side === "market" && (
          <div>
            <Tabs
              tabs={[
                { id: "charts", label: t("page.tabs.analytics"), icon: "show_chart" },
                { id: "table", label: t("page.tabs.table"), icon: "table_rows" },
              ]}
              active={marketTab}
              onChange={setMarketTab}
            />
            {marketTab === "charts" && <MarketAnalytics />}
            {marketTab === "table" && <MarketSessionsTable />}
          </div>
        )}
        {side === "adjustments" && <ListTransactionAdjustments setIsLoading={() => {}} />}
        {side === "bilateral" && <BilateralAllocationsSection />}
      </SideTabs>
    </div>
  );
}

export default ReadMarket;
